import {Component, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {concat, forkJoin, iif, Observable, of, switchMap, take, tap} from 'rxjs';
import {CoreService, ProleisObject, RequestParam, Task, TimetrackingBody, UserService,} from 'proleis-rest-client';
import {ActivatedRoute} from '@angular/router';
import {MultiTrackingComponent} from './multi.tracking.component';
import {DurationPipe} from './duration.pipe';
import {Store} from '@ngrx/store';
import {ProleisForMobileState} from '../../../store/store.index';
import {PlanningActions} from '../../../store/planning/planning.index';
import {map} from 'rxjs/operators';
import {Location} from '@angular/common';
import {BreakpointService, NotificationService} from 'proleis-web-app';
import {GUID_P4_EDIT_TASK_BOOKING_USER} from "../../../../p4m/guids";
import {LatestTrackingsComponent} from "./latest-trackings/latest-trackings.component";

export const FORM_MAPPING: Map<string, string> = new Map().set('date', 'base.date')
  .set('machineTracking', 'base.machineTracking')
  .set('workingTime', 'base.workingTime')
  .set('comment', 'base.description')
  .set('incidentId', 'base.incidentId')
  .set('resId', 'base.resId')
  .set('posId', 'additional.posId')
  .set('ratingTypeId', 'additional.ratingTypeId')
  .set('serviceTypeId', 'additional.serviceTypeId')
  .set('activityId', 'additional.activityId');


export function trackingValidator(us: UserService, ar: ActivatedRoute): AsyncValidatorFn {
  return (control: AbstractControl) => {
    const base = control.get('base');
    const body: TimetrackingBody = {
      workingTime: DurationPipe.toMinutes(
        base.get('workingTime').getRawValue(),
      ),
      date: base.get('date').getRawValue(),
      incidentId: base.get('incidentId').getRawValue(),
      resId: base.get('resId').getRawValue(),
      taskId: ar.snapshot.params.id,
      machineTracking: Boolean(base.get('machineTracking').getRawValue()),
      comment: base.get('description').getRawValue(),
    }

    return us.checkTimetrackingParams(body).pipe(
      take(1),
      map(checkResult => {
        if (checkResult.checkResults.length > 0) {
          let errors: ValidationErrors = {};
          const filteredResults = checkResult.checkResults?.filter(check => {
            return FORM_MAPPING.has(check.fieldName)
          });
          filteredResults.forEach(error => {
            errors[error.fieldName] = error;
            control.get(FORM_MAPPING.get(error.fieldName)).setErrors(errors);
          });
          return errors;
        }
        return null;
      })
    );
  }
}


@Component({
  selector: 'p4m-new-time-tracking-component',
  templateUrl: 'new-time-tracking.component.html',
  styleUrls: ['new-time-tracking.component.scss'],
})
export class NewTimeTrackingComponent implements OnInit {
  multiTracking = false;
  preliminaryTrackings: TimetrackingBody[];
  isMobile = false;
  errors: ValidationErrors | null;
  @ViewChild(MultiTrackingComponent) trackingRef: MultiTrackingComponent;

  @ViewChild('latestTrackings') latestTrackings: LatestTrackingsComponent;

  form: FormGroup;

  task$: Observable<Task>;
  res$: Observable<ProleisObject[]>;
  activities$: Observable<ProleisObject[]>;
  positions$: Observable<ProleisObject[]>;
  incidents$: Observable<ProleisObject[]>;
  serviceTypes$: Observable<ProleisObject[]>;
  ratingTypes$: Observable<ProleisObject[]>;
  users$: Observable<ProleisObject[]>;
  workDone$: Observable<number>;

  latestTrackingsExpanded = false;

  constructor(
    private fb: FormBuilder,
    private cs: CoreService,
    private ar: ActivatedRoute,
    private store: Store<ProleisForMobileState>,
    private us: UserService,
    private location: Location,
    private ns: NotificationService,
    private bs: BreakpointService
  ) {
  }

  ngOnInit() {
    this.loadTask();
    this.loadResources();
    this.loadActivities();
    this.loadIncidents();
    this.loadPositions();
    this.loadServiceTypes();
    this.loadValuationTypes();
    this.getWorkDonePercentage();
    this.loadUsers();
    this.initForm();

    this.preliminaryTrackings = [];

    this.bs.isSmall().subscribe(isMobile => {
      this.isMobile = isMobile;
    })
  }

  private initForm() {
    forkJoin({
      task: this.task$,
      resources: this.res$,
    }).subscribe((result) => {
      this.form = this.fb.group({
        base: this.fb.group({
          machineTracking: new FormControl(false),
          date: new FormControl(new Date(), [Validators.required]),
          workingTime: new FormControl(undefined, {
            validators: [
              Validators.required
            ],
          }),
          description: new FormControl(''),
          incidentId: new FormControl(result?.task?.PROBLEM_ID),
          resId: new FormControl(0),
          userId: new FormControl(0)
        }),
        additional: this.fb.group({
          posId: new FormControl(result?.task?.POSITION_OBJECT_ID),
          serviceTypeId: new FormControl(0),
          ratingTypeId: new FormControl(0),
          activityId: new FormControl(0),
        }),
      }, {asyncValidators: [trackingValidator(this.us, this.ar)], updateOn: 'blur'});
    });
  }

  private loadTask() {
    const aliasFields =
      `RESMACHINEBOOKING=RES_ID.MACHINE_BOOKING_MANUAL;`
      + `POSITION_OBJECT_ID=HEAD_ID.OBJECT_ID;CAN_TRACK_FOR_DIFFERENT_USER=$HASPERMISSION($GETOBJECTFROMGUID(${GUID_P4_EDIT_TASK_BOOKING_USER}))`;
    this.task$ = this.cs.getObjectById(this.ar.snapshot.params.id, aliasFields);
  }

  private loadResources() {
    this.res$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'RESOURCE_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING,
        };
        return iif(
          () => task.RES_ID > 0,
          of(undefined),
          this.cs.getObjectsQuery<ProleisObject[]>('getBDEPicklist', params),
        );
      }),
      tap(resources => {
        this.setDefaultResource(resources);
      }),
    );
  }

  setDefaultResource(resources: ProleisObject[]) {
    const userId = this.form?.get('base.userId').getRawValue();
    if (!userId) {
      this.us.getCurrentUser().subscribe((user) => {
        const foundRes = resources?.find(res => res.OBJECT_ID === user.DEFAULT_RES_ID);
        if (foundRes && this.form) {
          this.form.get('base').get('resId').setValue(foundRes.OBJECT_ID);
        }
      })
    } else {
      this.cs.getObjectById<ProleisObject>(userId).subscribe(user => {
        const foundRes = resources?.find(res => res.OBJECT_ID === user.DEFAULT_RES_ID);
        if (foundRes && this.form) {
          this.form.get('base').get('resId').setValue(foundRes.OBJECT_ID);
        }
      })
    }
    this.us.getCurrentUser().subscribe((user) => {
      const foundRes = resources?.find(res => res.OBJECT_ID === user.DEFAULT_RES_ID);
      if (foundRes && this.form) {
        this.form.get('base').get('resId').setValue(foundRes.OBJECT_ID);
      }
    })
  }

  private loadActivities() {
    this.activities$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'OPERATION_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING,
        };
        return this.cs.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      }),
    );
  }

  private loadPositions() {
    this.positions$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'POSITION_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING,
        };
        return this.cs.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      }),
    );
  }

  private loadIncidents() {
    this.incidents$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'INCIDENT_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING,
        };
        return this.cs.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      }),
    );
  }

  private loadServiceTypes() {
    this.serviceTypes$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'SERVICE_TYPE_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING,
        };
        return this.cs.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      }),
    );
  }

  private loadUsers() {
    this.users$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'USER_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING
        };
        return this.cs.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params
        )
      })
    )
  }

  private loadValuationTypes() {
    this.ratingTypes$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'TASK_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING,
          RESOURCE_ID: this.form.get('base').get('resId').value
        };
        return this.cs.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      })
    );
  }

  onRatingTypeChanged(event: boolean) {
    this.loadServiceTypes();
  }

  onResourceChanged(event: boolean) {
    this.loadValuationTypes();
  }

  getTrackingData(control?: AbstractControl): TimetrackingBody | null {
    let base: AbstractControl;
    if (control) {
      base = control.get('base');
    } else {
      base = this.form.get('base');
    }

    if (!base.valid) {
      return null;
    }

    const data: TimetrackingBody = {
      workingTime: DurationPipe.toMinutes(
        base.get('workingTime').getRawValue(),
      ),
      date: base.get('date').getRawValue(),
      incidentId: base.get('incidentId').getRawValue(),
      resId: base.get('resId').getRawValue(),
      userId: base.get('userId').getRawValue(),
      taskId: this.ar.snapshot.params.id,
      machineTracking: Boolean(base.get('machineTracking').getRawValue()),
      comment: base.get('description').getRawValue(),
    };
    const additional = this.form.get('additional');
    if (additional.valid) {
      data.posId = additional.get('posId').getRawValue();
      data.ratingTypeId = additional.get('ratingTypeId').getRawValue();
      data.serviceTypeId = additional.get('serviceTypeId').getRawValue();
    }
    return data;
  }

  createPreliminaryTracking() {
    if (!this.multiTracking) {
      this.multiTracking = true;
      setTimeout(() => {
        this.trackingRef.createPreliminaryTracking();
      }, 500);
    } else {
      this.trackingRef.createPreliminaryTracking();
    }
  }

  onTrackingsChanged(preliminaryTrackings: TimetrackingBody[]) {
    this.preliminaryTrackings = preliminaryTrackings;
  }

  createTrackings() {
    if (this.form.valid) {
      if (this.multiTracking) {
        const requests = [];
        this.preliminaryTrackings.forEach((tracking) => {
          requests.push(this.us.createTimeTracking(tracking));
        });
        concat(...requests).subscribe((result) => {
          this.location.back();
          this.ns.showNotification('Buchungen angelegt');
        });
      } else {
        this.store.dispatch(
          PlanningActions.createTimeTracking({
            body: this.getTrackingData(),
          }),
        );
      }
    }
  }

  getWorkDonePercentage() {
    this.workDone$ = this.task$.pipe(
      map((task) => (task.ARBEITIST / task.ARBEITPLAN) * 100),
    );
  }

  onMachineTrackingChanged(event) {
    this.loadPositions();
  }


  getErrorMessage(control: AbstractControl, fieldName: string): string | null {
    if (!control || !control.errors || control.pending) {
      return null;
    }
    console.log(control.status)
    return control.errors[fieldName]?.errorMessage;
  }

  onUserChanged(event: any) {
    this.loadResources();
  }

  onLatestTrackingsExpandedChanged(expanded: boolean) {
    this.latestTrackingsExpanded = expanded;
  }

  refreshLatestTrackings() {
    this.latestTrackingsExpanded = false;
    this.latestTrackings.loadTrackings();
  }

}
